import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  line, Color, LIST_PAGES, NEW_DOCUMENT_ID, Size, documentPageMode, releasedForWorkProblem, resolvedStatusPill, salesOrderPills, pageKindLineText, t, type SalesOrderAnswer, documentNumberText } from '@bms2/shared';
import { Button, Dropdown, NumericInput, TextInput } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { DetailList, DetailRow } from '../../components/composites/DetailList';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { Modal } from '../../components/modals/Modal';
import { ReasonWindow } from '../../components/modals/ReasonWindow';
import modalStyles from '../../components/modals/modal.module.css';
import { AccessDenied } from '../../shell/Fallbacks';
import { useStatusResolver } from '../../shell/StatusResolver';
import { CHANGE_ORDER_ROUTE } from './ChangeOrderPage';
import { BannerPills } from './BannerPills';
import { DocumentViewPage, listPricedCell, type DocumentLinesDescriptor } from './DocumentViewPage';
import { DocumentFilesPanel } from './DocumentFilesPanel';
import { SourceLinkPanel } from './SourceLinkPanel';
import { SalesOrderForm } from './SalesOrderForm';
import { SalesOrderRaiseControls, raiseWindowOf } from './SalesOrderRaise';
import { OrderPackageWindows, OrderPackagesBlocks, useOrderPackages, type OrderPackageWindow } from './SalesOrderPackages';
import { WhatsNextBanner } from '../../components/composites/WhatsNextBanner';
import { ApprovalDecision } from '../approvals/ApprovalDecision';
import { ApprovalReason, SendHint, draftSendLabel } from './ApprovalReason';
import { SALES_ORDERS_PATH, get, patch, post, useApi } from './api';
import { RaiseInvoiceWindow, invoiceFromOrderPath, invoiceShippingChargesPath, type UnbilledShippingCharge } from './RaiseInvoiceWindow';
import { usePersonSearch } from '../settings/api';
import { documentPdfPath } from './ViewPdfLink';
import { type ScreenFact } from './PageFacts';
import styles from './DocumentPage.module.css';
import { Money } from '../../components/Money';
import { DealTeamPanel } from './DealTeamPanel';
import { useViewerZone } from '../../shell/ViewerZone';
import { RecordMessagesPanel } from '../mail/RecordMessagesPanel';
import { DeliveredPanel } from './DeliveredPanel';
import { StaleSendBanner, staleSendFor } from './StaleSendWarning';
import { DocumentSendWindow, type DocumentSendFields } from './DocumentSendWindow';
import { useAddNew } from './AddNewHost';
import { DocumentNotes, carriedNotes } from './DocumentNotes';
import { useDayText } from '../../shell/viewerDates';

type BranchKey = 'partsBranchStatus' | 'labourBranchStatus';
/** The order's two branches, parts first, as the block draws them. */
const BRANCHES: readonly { key: BranchKey; label: () => string }[] = [
  { key: 'partsBranchStatus', label: () => t('salesOrder.branches.parts') },
  { key: 'labourBranchStatus', label: () => t('salesOrder.branches.labour') },
];

/** The three axes as pills: the lifecycle, the approval and the fulfilment, each read off the one status vocabulary. */
type OpenModal = 'send' | 'markSent' | 'customerPo' | 'waive' | 'void' | null;

const PRIMARY_LABEL: Record<'send' | 'recordCustomerPo' | 'release' | 'invoice', () => string> = {
  send: () => t('salesOrder.action.send'),
  recordCustomerPo: () => t('salesOrder.action.recordCustomerPo'),
  release: () => t('salesOrder.action.release'),
  invoice: () => t('salesOrder.action.invoice'),
};

/**
 * 🔒 The Sales Order's page (piece 22; decision 0409): view mode draws Regions
 * 1 to 10 over the projection the server built, then the order's own lines, its
 * branches and its change orders beneath; edit mode is the form. The footer
 * carries the status's one primary action, the waiver where the customer's
 * number is holding the release, Raise a change order and Void.
 *
 * 🔨 Every action is a route the server refuses on its own terms; the page only
 * offers what the status allows and never decides the refusal itself.
 */
export function SalesOrderPage({ orderId, apiBaseUrl = '' }: { orderId: string; apiBaseUrl?: string }) {
  const dayText = useDayText();
  const addNew = useAddNew(apiBaseUrl);
  // 🔒 The banner's pills read the shell's one status resolver, as the lists do (decision 0639).
  const resolveStatus = useStatusResolver();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // 🔒 The URL carries the mode, so a link opens the mode it names (decision 0409); `new` is the Draft + New opens.
  const creating = orderId === NEW_DOCUMENT_ID;
  const editing = documentPageMode(params.get('mode'), orderId) === 'edit';
  const page = useApi<SalesOrderAnswer>(editing ? null : `${SALES_ORDERS_PATH}/${orderId}`, apiBaseUrl);
  // 🔒 The order's packages and shipments, read beside the order and reloaded with it (decision 1730).
  const packaging = useOrderPackages(editing || creating ? null : orderId, apiBaseUrl);
  const zone = useViewerZone();
  const [modal, setModal] = useState<OpenModal>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [packWindow, setPackWindow] = useState<OrderPackageWindow>(null);
  const [busy, setBusy] = useState(false);
  const [charges, setCharges] = useState<UnbilledShippingCharge[] | null>(null);
  const [chargesError, setChargesError] = useState<string | null>(null);
  const [customerPo, setCustomerPo] = useState('');
  const [poAmount, setPoAmount] = useState<number | null>(null);
  const [authoriser, setAuthoriser] = useState<{ id: string; label: string } | null>(null);
  const personSearch = usePersonSearch(apiBaseUrl);

  // Save and Send lands here with `act=send`, so the send opens on the saved order rather than on a form that has not written yet.
  const asked = params.get('act');
  const [askDone, setAskDone] = useState(false);
  const open: OpenModal = modal ?? (asked === 'send' && !editing && !askDone ? 'send' : null);
  // 🔒 Closing a window clears what it held outside its own text too, so a cancelled Waive reopens with no authorizer chosen (decision 2870).
  const close = () => {
    setModal(null);
    setAskDone(true);
    setAuthoriser(null);
  };

  if (editing) return <SalesOrderForm salesOrderId={creating ? null : orderId} apiBaseUrl={apiBaseUrl} />;
  if (page.denied) return <AccessDenied reason={page.deniedReason} />;

  const act = async <T,>(path: string, body: unknown, ok: string | ((data: T) => string)) => {
    setBusy(true);
    const answer = await post<T>(`${SALES_ORDERS_PATH}/${orderId}/${path}`, body, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) {
      setNotice(`!${answer.message}`);
      return false;
    }
    close();
    setNotice(typeof ok === 'string' ? ok : ok(answer.data));
    page.reload();
    return true;
  };

  /** 🔒 Send mails the order's own PDF and says who it went to, or that the gate held it for approval (decision 2630). */
  const sendOrder = (fields: DocumentSendFields, sendAnyway: boolean) =>
    void act<{ to: string; sent: string; submitted?: boolean }>('send', { ...fields, ...(sendAnyway ? { sendAnyway: true } : {}) }, (data) =>
      data.submitted ? t('approval.submitted') : data.sent === 'Accepted' || data.sent === 'Deferred' ? line(t('document.send.sentTo'), data.to) : t('document.send.failed'),
    );

  /** 🔒 Mark N/A and its undo write the one branch through the order's own route, which recomputes the fulfilment (T913; decision 1711). */
  const setBranch = async (key: BranchKey, to: 'NotApplicable' | 'NotStarted', ok: string) => {
    setBusy(true);
    const answer = await patch<unknown>(`${SALES_ORDERS_PATH}/${orderId}/branches`, { [key]: to }, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setNotice(`!${answer.message}`);
    setNotice(ok);
    page.reload();
  };

  /** 🔒 Raise an Invoice asks first for the shipping charges the order has not billed, and raises straight away where there are none (decision 2531). */
  const raiseInvoice = async () => {
    setBusy(true);
    const offered = await get<{ rows: UnbilledShippingCharge[] }>(invoiceShippingChargesPath(orderId), apiBaseUrl);
    // A charges read that failed raises nothing: raising on would leave the order's shipping charges unbilled without a word (T1813).
    if (!offered.ok) {
      setBusy(false);
      return setNotice(`!${offered.message}`);
    }
    if (offered.data.rows.length > 0) {
      setBusy(false);
      return setCharges(offered.data.rows);
    }
    await raiseInvoiceWith([]);
  };
  const raiseInvoiceWith = async (shippingShipmentIds: string[]) => {
    setBusy(true);
    const answer = await post<{ id: string }>(invoiceFromOrderPath(orderId), { shippingShipmentIds }, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) {
      if (charges) return setChargesError(answer.message);
      return setNotice(`!${answer.message}`);
    }
    // The invoice is raised as a Draft and the person lands on it, the way a create always does.
    navigate(`${LIST_PAGES.Invoice.route}/${answer.data.id}`);
  };

  return (
    <>
    <AsyncStatePanel state={page.state} emptyAction={t('document.notFound')} onRetry={page.reload}>
      {(order) => {
        const facts: ScreenFact[] = [
          { label: t('salesOrder.fact.account'), value: order.companyName, route: `/companies/${order.companyId}` },
          { label: t('salesOrder.fact.deal'), value: order.dealName, route: order.dealId ? `/deals/${order.dealId}` : null },
          { label: t('salesOrder.fact.quote'), value: order.convertedFrom, route: order.sourceQuoteId ? `/quotes/${order.sourceQuoteId}` : null },
          { label: t('salesOrder.fact.customerPo'), value: order.customerPoNumber },
          // 🔒 Decision 2594 (T1175): the amount the customer's PO covers, beside its number.
          { label: t('jobBilling.po.amount'), value: order.customerPoAmountCents == null ? null : <Money cents={order.customerPoAmountCents} currencyCode={order.currencyCode} /> },
          // 🔒 The Deal's not-to-exceed, read where one stands (decision 2612).
          ...(order.notToExceedCents == null ? [] : [{ label: t('quote.notToExceed'), value: <Money cents={order.notToExceedCents} currencyCode={order.currencyCode} /> }]),
          { label: t('salesOrder.fact.contractValue'), value: <Money cents={order.contractValueCents} currencyCode={order.currencyCode} /> },
          { label: t('salesOrder.fact.invoiced'), value: <Money cents={order.invoicedCents} currencyCode={order.currencyCode} /> },
          { label: t('document.fact.date'), value: order.documentDate },
          { label: t('salesOrder.fact.created'), value: dayText(order.createdAt) },
        ];
        // 🔒 Decision 1225: the lifecycle pill reads Sending or Delivery Failed over a Sent whose send the host has not confirmed.
        const shownStatus = order.displayedStatus ?? order.status;
        const stale = staleSendFor('SalesOrder', order.staleSend, zone);
        const pills = salesOrderPills({ ...order, status: shownStatus }, resolveStatus);
        const primary = order.footer?.primary ?? null;
        // 🔒 The waiver stands while the release waits on either half, the number or the document (T917; decision 1693).
        const offersWaiver = order.footer?.mayWaive === true;
        // 🔒 Packages are packed and shipped on a Released or Closed order its viewer may write (decisions 1730, 1731).
        // 🔒 …and only while a goods line has something left that no drop-ship order covers (decisions 1930, 1931); an older reply carries no answer and keeps them.
        const packs = releasedForWorkProblem(order.status) === null && !order.writeRefusal && order.next?.shipLeft !== false;
        const openInvoiceId = order.next?.steps.includes('recordPayment') ? order.next.openInvoiceId ?? null : null;
        // 🔒 The customer PO modal opens on what the order already carries, number and amount (decision 2594).
        const openCustomerPo = () => {
          setCustomerPo(order.customerPoNumber ?? '');
          setPoAmount(order.customerPoAmountCents ?? null);
          setModal('customerPo');
        };
        // 🔒 A sent or released order takes its PO amount from the footer; a Draft types it on its form (decision 2594).
        const setsPoAmount = order.status !== 'Draft' && order.status !== 'Void' && !order.writeRefusal && Boolean(order.customerPoNumber) && primary !== 'recordCustomerPo';
        // 🔒 A Draft's Send carries Mark as Sent behind its chevron, the Quote's split control (decision 2630); Submit for approval stands alone.
        const submitsForApproval = primary === 'send' && order.approval?.sendAction === 'submitForApproval';
        const footer = (
          <>
            {/* 🔒 The approval decision on the document it is about (decision 0599, on decision 0504's reasoning). */}
            <ApprovalDecision
              documentType="SalesOrder"
              documentId={order.id}
              canApprove={order.canApprove}
              apiBaseUrl={apiBaseUrl}
              onDone={(message) => {
                setNotice(message);
                page.reload();
              }}
              onError={(message) => setNotice(`!${message}`)}
            />
            {/* 🔨 Edit reopens the form on a Draft alone and is the action bar's primary, drawn there once (decisions 0409, 0612). */}
            {primary ? (
              <Button
                size={Size.Medium}
                color={Color.Navy}
                /* 🔒 A Draft's one primary action reads what the gate's dry run decides, and the press does what it reads (T223; decision 0713). */
                text={submitsForApproval ? [draftSendLabel(order.approval, PRIMARY_LABEL.send())] : primary === 'send' ? [PRIMARY_LABEL.send(), t('document.action.markSent')] : [PRIMARY_LABEL[primary]()]}
                disabled={busy}
                actions={[
                  () => {
                    if (submitsForApproval) void act('send', {}, t('approval.submitted'));
                    else if (primary === 'send') setModal('send');
                    else if (primary === 'release') void act('release', {}, t('salesOrder.released'));
                    else if (primary === 'invoice') void raiseInvoice();
                    else openCustomerPo();
                  },
                  ...(primary === 'send' && !submitsForApproval ? [() => setModal('markSent')] : []),
                ]}
              />
            ) : null}
            {/* 🔒 An order already out takes Send again, which mails the copy the customer holds and moves nothing (decision 2630). */}
            {order.footer?.mayResend ? (
              <span data-sales-order-action="resend">
                <Button size={Size.Medium} text={[t('document.send.again')]} actions={[() => setModal('send')]} disabled={busy} />
              </span>
            ) : null}
            <SendHint view={order.approval} />
            {setsPoAmount ? <Button size={Size.Medium} text={[t('jobBilling.po.setAmount')]} actions={[openCustomerPo]} disabled={busy} /> : null}
            {offersWaiver ? <Button size={Size.Medium} text={[t('salesOrder.action.waiveCustomerPo')]} actions={[() => setModal('waive')]} disabled={busy} /> : null}
            {/*
              * 🔒 Raise a change order opens the change order's own page in edit mode
              * (decision 0475), the way + New opens every other document's. It captured
              * a reason and a description in a modal and no lines at all, so every change
              * order raised on this screen stood at $0.00 for good.
              */}
            {order.footer?.mayVoid ? <Button size={Size.Medium} text={[t('salesOrder.action.changeOrder')]} actions={[() => navigate(`${CHANGE_ORDER_ROUTE}/${NEW_DOCUMENT_ID}?salesOrderId=${order.id}`)]} disabled={busy} /> : null}
            {order.footer?.mayVoid ? <Button size={Size.Medium} color={Color.Inactive} text={[t('salesOrder.action.void')]} actions={[() => setModal('void')]} disabled={busy} /> : null}
            {order.footer?.refusal ? <span className={styles.refusal} data-sales-order-refusal="true">{order.footer.refusal}</span> : null}
            {/* 🔨 The sheet's own control moved to the action bar above both views, so one document offers it once (decision 0612). */}
            {/* 🔒 The order's two branches, raised from its footer bar as outlined buttons (decision 1440, moving decision 0863's title-bar pair; T685); the to-do opens the same raise. */}
            {order.raise && !order.writeRefusal ? <SalesOrderRaiseControls orderId={order.id} view={order.raise} lines={order.lines} askedFor={raiseWindowOf(asked)} apiBaseUrl={apiBaseUrl} /> : null}
            {/* 🔒 Create package and Create shipment stand on a Released or Closed order its viewer may write (decisions 1730, 1731). */}
            {packs ? <Button size={Size.Medium} text={[t('salesOrder.action.createPackage')]} actions={[() => setPackWindow('package')]} disabled={busy} /> : null}
            {packs ? <Button size={Size.Medium} text={[t('salesOrder.action.createShipment')]} actions={[() => setPackWindow('ship')]} disabled={busy} /> : null}
          </>
        );
        const moneyOf = (amount: number) => <Money cents={amount} currencyCode={order.currencyCode} />;
        /** 🔒 The lines as the one read-only table draws them for every kind (decision 0612). */
        const lines: DocumentLinesDescriptor = {
          title: t('salesOrder.lines.title'),
          rowAttribute: 'data-sales-order-line',
          blockAttribute: 'data-sales-order-lines',
          rows: order.lines.map((row) => ({
            id: row.id,
            label: row.sku ? line(row.sku, '·', row.description) : row.description,
            // 🔒 An allowance line says so beside where it stands on billing (decision 2612).
            note: row.isAllowance ? line(t('item.allowance'), '·', row.invoicedLabel) : row.invoicedLabel,
            noteAttributes: { 'data-sales-order-invoiced': row.invoicedLabel, ...(row.isAllowance ? { 'data-sales-order-allowance': 'true' } : {}) },
            cells: [line(String(row.quantity), row.unitName), row.unitPriceCents === null ? '' : listPricedCell(moneyOf(row.unitPriceCents), row.listPriceCents, order.currencyCode)],
            amount: moneyOf(row.amountCents),
            taxable: row.taxable === true,
          })),
          totals: [{ label: t('salesOrder.lines.total'), value: moneyOf(order.totalCents), attributes: { 'data-sales-order-total': String(order.totalCents) } }],
        };
        // 🔒 One notes panel, placed in the Details view and handed to the PDF view's rail; only one view is mounted (T587; decision 1403).
        const carried = carriedNotes(order);
        const notesPanel = <DocumentNotes recordType="SalesOrder" recordId={order.id} recordLabel={order.salesOrderNumber} apiBaseUrl={apiBaseUrl} onError={(message) => setNotice(`!${message}`)} carried={carried} />;
        // One Files block, placed in the Details view and handed to the PDF view's rail as its Files tab (T1070; decision 1662).
        const filesPanel = (
          <section className={styles.block} data-sales-order-files={order.files.length}>
            <h2 className={styles.heading}>{t('documentFiles.title')}</h2>
            <DocumentFilesPanel
              recordType="SalesOrder"
              recordId={order.id}
              files={order.files}
              apiBaseUrl={apiBaseUrl}
              onChanged={page.reload}
              writeRefusal={order.writeRefusal ?? null}
            />
          </section>
        );
        return (
          <DocumentViewPage
            notes={carried === null ? null : notesPanel}
            files={filesPanel}
            documentType="SalesOrder"
            icon="terms"
            title={documentNumberText('SalesOrder', order.salesOrderNumber, order.revision)}
            subtitle={pageKindLineText({ page: 'document', documentType: 'SalesOrder' })}
            information={facts}
            status={<BannerPills pills={pills} attribute="data-sales-order-pills" hint={order.approval?.reason ?? null} />}
            footer={footer}
            footerAttributes={{ 'data-sales-order-actions': primary ?? 'none' }}
            pageAttributes={{ 'data-sales-order': order.id, 'data-sales-order-status': order.status, 'data-sales-order-approval': order.approvalStatus }}
            print={order.printDocument ?? null}
            layout={order.printLayout ?? null}
            sheet={order.printSheet ?? null}
            writeRefusal={order.writeRefusal ?? null}
            ribbon={shownStatus === order.status ? order.status : (pills[0]?.text ?? order.status)}
            ribbonStatus={shownStatus}
            pdfPath={documentPdfPath(SALES_ORDERS_PATH, order.id)}
            listRoute={LIST_PAGES.SalesOrder.route}
            {...(order.editable ? { onEdit: () => navigate(`${LIST_PAGES.SalesOrder.route}/${order.id}?mode=edit`) } : {})}
            apiBaseUrl={apiBaseUrl}
            terms={{ documentId: order.id, onSaved: page.reload }}
            lines={lines}
            whatsNext={
              /* 🔒 The footer's own moves, named as the next step (decision 1732). */
              <WhatsNextBanner
                documentType="SalesOrder"
                status={order.status}
                disabled={busy}
                /* 🔒 The order's state, not its status alone, names what is left (decision 1931). */
                next={order.writeRefusal ? null : order.next ?? null}
                actions={{
                  ...(packs ? { createPackage: () => setPackWindow('package'), createShipment: () => setPackWindow('ship') } : {}),
                  ...(primary === 'invoice' ? { raiseInvoice: () => void raiseInvoice() } : {}),
                  ...(openInvoiceId ? { recordPayment: () => navigate(`${LIST_PAGES.Invoice.route}/${openInvoiceId}`) } : {}),
                }}
              />
            }
            notices={
              <>
                {notice ? <AlertBanner tone={notice.startsWith('!') ? 'error' : 'good'} message={notice.replace(/^!/, '')} action={{ label: t('modal.dismiss'), onAction: () => setNotice(null) }} /> : null}
                {/* 🔒 The approval block says why the order rests where it does (T131; decision 0713); an older answer carries the escalation alone. */}
                {order.approval ? <ApprovalReason view={order.approval} /> : order.escalationReason ? <p className={styles.sentence} data-sales-order-escalation>{line(t('po.escalation'), '·', order.escalationReason)}</p> : null}
                {order.rejectionReason ? <p className={styles.rejection} data-sales-order-rejection>{line(t('po.rejection'), '·', order.rejectionReason)}</p> : null}
                {/* 🔒 The account over its credit limit or on Credit Hold, so the salesperson has the talk; it warns and blocks nothing (decision 3280; T2269). */}
                {order.creditWarning ? <div data-sales-order-credit-warning="true"><AlertBanner tone="warning" urging message={order.creditWarning} /></div> : null}
                {/* 🔒 Billed past the customer's PO amount, warned and never refused (decision 2594; T1175). */}
                {order.customerPoWarning ? <div data-sales-order-po-warning="true"><AlertBanner tone="warning" urging message={order.customerPoWarning} /></div> : null}
                {order.customerPoRequired && order.customerPoReason ? <p className={styles.sentence} data-sales-order-po-reason>{order.customerPoReason}</p> : null}
              </>
            }
            details={
              <>
              {/* 🔒 The Deal's team as it stood when this document was created (decision 0765). */}
              <DealTeamPanel team={order.dealTeam} timeZone={zone} />
              {/* 🔒 The mail sent about this order and its link's opens (decisions 0162, 0791). */}
              <RecordMessagesPanel recordType="SalesOrder" recordId={order.id} timeZone={zone} apiBaseUrl={apiBaseUrl} />
              {/* 🔒 Where the newest send stands, one line per recipient (decisions 1225, 1226). */}
              <DeliveredPanel delivery={order.delivery ?? null} timeZone={zone} />
              {/*
                * 🔒 Both branches with Mark N/A on one not started and its undo on one marked (T913; decision 1711). The documents
                * move the rest: a purchase order or a shipment the parts branch, a Work Order the labour branch.
                */}
              <section className={styles.block} data-sales-order-branches={`${order.partsBranchStatus} ${order.labourBranchStatus}`}>
                <h2 className={styles.heading}>{t('salesOrder.branches.title')}</h2>
                <DetailList look="separator">
                  {BRANCHES.map(({ key, label }) => {
                    const status = order[key];
                    const marks = !order.writeRefusal && order.status !== 'Void' && order.fulfillmentStatus !== 'Cancelled';
                    return (
                      <DetailRow
                        key={key}
                        data-sales-order-branch={key}
                        data-branch-status={status}
                        lead={<span className={styles.sentence}>{line(label(), resolvedStatusPill(resolveStatus, 'BranchStatus', status).text)}</span>}
                        actions={
                          <>
                            {marks && status === 'NotStarted' ? <Button size={Size.Small} text={[t('salesOrder.branches.markNotApplicable')]} actions={[() => void setBranch(key, 'NotApplicable', t('salesOrder.branches.markedNotApplicable'))]} disabled={busy} /> : null}
                            {marks && status === 'NotApplicable' ? <Button size={Size.Small} text={[t('salesOrder.branches.undoNotApplicable')]} actions={[() => void setBranch(key, 'NotStarted', t('salesOrder.branches.undoneNotApplicable'))]} disabled={busy} /> : null}
                          </>
                        }
                      />
                    );
                  })}
                </DetailList>
              </section>

              {/* 🔒 A change order is raised and listed on its parent order's page, and a row opens its own page (decision 0475); it has no list of its own. */}
              <section className={styles.block} data-sales-order-change-orders={order.changeOrders.length}>
                <h2 className={styles.heading}>{t('salesOrder.changeOrders.title')}</h2>
                {order.changeOrders.length === 0 ? <p className={styles.muted}>{t('salesOrder.changeOrders.none')}</p> : null}
                <DetailList look="separator">
                  {order.changeOrders.map((change) => (
                    <DetailRow
                      key={change.id}
                      data-change-order={change.id}
                      title={<Link to={`${CHANGE_ORDER_ROUTE}/${change.id}`}>{change.changeOrderNumber}</Link>}
                      detail={line(change.reason, '·', change.description)}
                      columns={[
                        { value: resolvedStatusPill(resolveStatus, 'ChangeOrderStatus', change.status).text },
                        { value: resolvedStatusPill(resolveStatus, 'ApprovalStatus', change.approvalStatus).text },
                      ]}
                    >
                      <span className={styles.rowAmount}><Money cents={change.totalCents} currencyCode={order.currencyCode} /></span>
                    </DetailRow>
                  ))}
                </DetailList>
                {order.pendingChangeCents > 0 ? <p className={styles.total} data-sales-order-pending={order.pendingChangeCents}>{t('salesOrder.fact.contractValue')} <Money cents={order.pendingChangeCents} currencyCode={order.currencyCode} /></p> : null}
              </section>

              {/* 🔒 The order's packages and the shipments raised from them (decisions 1730, 1731). */}
              <OrderPackagesBlocks read={packaging} currencyCode={order.currencyCode} apiBaseUrl={apiBaseUrl} />

              {/* 🔒 The files attached to this order, and the customer's own purchase order among them (decision 0457). */}
              {filesPanel}

              {/* 🔒 The quote this order came from, named after the fact and only while it is a Draft (decision 0469). */}
              <SourceLinkPanel
                path={SALES_ORDERS_PATH}
                recordId={order.id}
                sourceKind="Quote"
                sourceLabel={order.convertedFrom}
                sourceRoute={order.sourceQuoteId ? `/quotes/${order.sourceQuoteId}` : null}
                editable={order.status === 'Draft'}
                apiBaseUrl={apiBaseUrl}
                onChanged={page.reload}
              />
              {/* 🔒 The document's notes, the composer at the top of the list (decision 0951; T336). */}
              {notesPanel}
              </>
            }
          >
              {/* 🔒 Raise an Invoice, where the order has shipping charges nobody has billed yet (T1132; decision 2531). */}
              {charges ? <RaiseInvoiceWindow charges={charges} busy={busy} error={chargesError} onRaise={(ids) => void raiseInvoiceWith(ids)} onCancel={() => { setCharges(null); setChargesError(null); }} onDismissError={() => setChargesError(null)} /> : null}
              <OrderPackageWindows
                salesOrderId={order.id}
                open={packWindow}
                onOpen={setPackWindow}
                apiBaseUrl={apiBaseUrl}
                onNotice={setNotice}
                onChanged={() => {
                  page.reload();
                  packaging.reload();
                }}
              />
              {open === 'send' ? (
                /* 🔒 The send window, the Quote's over this order: To, Message, the stale warning and the last send (decision 2630). */
                <DocumentSendWindow
                  title={t('salesOrder.mail.title')}
                  dataModal="sales-order-mail"
                  sendLabel={t('salesOrder.action.send')}
                  hints={order.status === 'Draft' ? [t('salesOrder.mail.hint')] : [t('salesOrder.mail.resendHint'), t('salesOrder.mail.hint')]}
                  stale={stale}
                  previousDelivery={order.delivery ?? null}
                  timeZone={zone}
                  busy={busy}
                  onSend={sendOrder}
                  onCancel={close}
                  mailbox={order.sendMailbox ?? null}
                  onChangeDate={() => {
                    close();
                    navigate(`${LIST_PAGES.SalesOrder.route}/${order.id}?mode=edit`);
                  }}
                />
              ) : null}
              {open === 'markSent' ? (
                <Modal
                  open
                  title={t('salesOrder.send.title')}
                  onCancel={close}
                  dataModal="sales-order-send"
                  actions={[
                    /* 🔒 Mark as Sent makes every move Send makes except the mail, and its audit row is its own action (decisions 0436, 2630). */
                    { kind: 'primary', label: stale ? t('document.staleSend.sendAnyway') : t('document.action.markSent'), onAct: () => void act('mark-sent', stale ? { sendAnyway: true } : {}, t('document.markedSent')), disabled: busy },
                    { kind: 'cancel', label: t('modal.cancel'), onAct: close },
                  ]}
                >
                  {/* 🔒 A first send of an old-dated order warns, and Send Anyway is what the audit row records (decision 1227). */}
                  <StaleSendBanner
                    warning={stale}
                    onChangeDate={() => {
                      close();
                      navigate(`${LIST_PAGES.SalesOrder.route}/${order.id}?mode=edit`);
                    }}
                  />
                  <p className={modalStyles.fieldLabel}>{t('salesOrder.send.hint')}</p>
                </Modal>
              ) : null}
              {open === 'customerPo' ? (
                <Modal
                  open
                  title={t('salesOrder.customerPo.title')}
                  onCancel={close}
                  dataModal="sales-order-customer-po"
                  actions={[
                    { kind: 'primary', label: t('salesOrder.action.recordCustomerPo'), onAct: () => void act('customer-po', { customerPoNumber: customerPo.trim(), customerPoAmountCents: poAmount }, t('salesOrder.customerPo.recorded')), disabled: busy || !customerPo.trim() },
                    { kind: 'cancel', label: t('modal.cancel'), onAct: close },
                  ]}
                >
                  <TextInput size={Size.Small} label={t('salesOrder.customerPo.number')} value={customerPo} onChange={setCustomerPo} required name="customerPoNumber" />
                  <NumericInput size={Size.Medium} kind="currency" label={t('jobBilling.po.amount')} value={poAmount} onChange={setPoAmount} name="customerPoAmount" hint={t('jobBilling.po.amountHint')} />
                </Modal>
              ) : null}
              {open === 'waive' ? (
                <ReasonWindow
                  open
                  title={t('salesOrder.waive.title')}
                  label={t('salesOrder.waive.reason')}
                  actLabel={t('salesOrder.action.waiveCustomerPo')}
                  busy={busy}
                  ready={authoriser !== null}
                  notReadyReason={t('salesOrder.waive.needsAuthoriser')}
                  onCancel={close}
                  refusal={notice?.startsWith('!') ? notice.slice(1) : null}
                  dataModal="sales-order-waive"
                  onAct={(reason) => act('waive-customer-po', { reason, authorisedByPersonId: authoriser?.id ?? null }, t('salesOrder.waive.waived'))}
                >
                  <Dropdown size={Size.Medium} label={t('salesOrder.waive.authoriser')} entityLabel={t('salesOrder.waive.authoriserEntity')} search={personSearch} value={authoriser?.label ?? null} onSelect={(row) => setAuthoriser({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'employee' }, (row) => setAuthoriser({ id: row.id, label: row.line1 }))} required />
                </ReasonWindow>
              ) : null}
              {open === 'void' ? (
                <ReasonWindow
                  open
                  title={t('salesOrder.void.title')}
                  label={t('salesOrder.void.reason')}
                  actLabel={t('salesOrder.action.void')}
                  hint={t('salesOrder.void.hint')}
                  busy={busy}
                  onCancel={close}
                  refusal={notice?.startsWith('!') ? notice.slice(1) : null}
                  dataModal="sales-order-void"
                  onAct={(reason) => act('void', { reason }, t('salesOrder.voided'))}
                />
              ) : null}
          </DocumentViewPage>
        );
      }}
    </AsyncStatePanel>
    {addNew.host}
    </>
  );
}
